import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  Space,
  Typography,
  Divider,
  Tag,
  message,
  Spin,
  Checkbox,
  Radio,
  Tooltip,
  Alert,
  Progress,
  Upload,
  List,
  Popconfirm
} from 'antd';
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  AimOutlined,
  CalendarOutlined,
  FileTextOutlined,
  LinkOutlined,
  UploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  PlusOutlined
} from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import ActionManagementComponent from './ActionManagementComponent';
import MomentDatePicker from './MomentDatePicker.tsx';
import EducationMaterialsManager from './EducationMaterialsManager';
import ReviewSchedulingComponent from './ReviewSchedulingComponent';
import PatientGoalEditForm from './PatientGoalEditForm';
import { ORGAN_SYSTEM_DISPLAY_NAMES } from '../utils/organSystemConstants';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text, Paragraph } = Typography;

// Utility function to decode HTML entities
const decodeHtmlEntities = (text) => {
  if (typeof text !== 'string') return text;

  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

// Utility function to format dates in text from dd/mm/yyyy to MMM DD, YYYY
const formatDatesInText = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  // Regex to match dd/mm/yyyy format (with optional dd/mm/yy)
  const dateRegex = /\b(\d{1,2})\/(\d{1,2})\/(\d{2,4})\b/g;
  
  return text.replace(dateRegex, (match, day, month, year) => {
    try {
      // Convert 2-digit year to 4-digit year if needed
      let fullYear = year;
      if (year.length === 2) {
        const currentYear = new Date().getFullYear();
        const currentCentury = Math.floor(currentYear / 100) * 100;
        fullYear = currentCentury + parseInt(year);
        // If the year is more than 30 years in the future, assume it's from the previous century
        if (fullYear > currentYear + 30) {
          fullYear -= 100;
        }
      }
      
      // Create date object (month is 0-indexed in JavaScript)
      const date = new Date(parseInt(fullYear), parseInt(month) - 1, parseInt(day));
      
      // Validate the date is valid
      if (date.getDate() !== parseInt(day) || 
          date.getMonth() !== parseInt(month) - 1 || 
          date.getFullYear() !== parseInt(fullYear)) {
        return match; // Return original if invalid date
      }
      
      // Format using moment for consistency with the rest of the app
      return moment(date).format('MMM DD, YYYY');
    } catch (error) {
      // Return original text if date parsing fails
      return match;
    }
  });
};

const PILLAR_COLORS = {
  'Medication': '#1890ff',
  'Nutrition': '#52c41a',
  'Movement': '#fa8c16',
  'Recovery': '#722ed1',
  'Emotion': '#eb2f96'
};

const STATUS_COLORS = {
  'Not Started': '#d9d9d9',
  'In Progress': '#1890ff',
  'Completed': '#52c41a',
  'On Hold': '#fa8c16',
  'Cancelled': '#f5222d'
};

const TIER_COLORS = {
  1: '#52c41a',
  2: '#1890ff',
  3: '#fa8c16',
  4: '#f5222d'
};

const EnhancedSmartGoalDetailsModal = ({
  visible,
  onCancel,
  onUpdate,
  onDelete,
  goal,
  readOnly = false,
  initialEditing = false
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(initialEditing);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddGoalForm, setShowAddGoalForm] = useState(false);
  const [addGoalForm] = Form.useForm();
  const [activePatientGoalIndex, setActivePatientGoalIndex] = useState(0);
  const [currentGoal, setCurrentGoal] = useState(goal);
  const [editingPatientGoalId, setEditingPatientGoalId] = useState(null);

  // Update currentGoal when goal prop changes
  useEffect(() => {
    setCurrentGoal(goal);
  }, [goal]);

  // Initialize form when goal changes
  useEffect(() => {
    if (currentGoal && visible) {
      // Set overall goal data
      form.setFieldsValue({
        status: currentGoal.status,
        mdSmartGoalOrder: currentGoal.mdSmartGoalOrder,
        clientFeedback: currentGoal.clientFeedback || '',
        hmReviewNote: currentGoal.hmReviewNote || ''
      });

      // Reset active patient goal index if needed
      if (currentGoal.patientGoals && currentGoal.patientGoals.length > 0) {
        setActivePatientGoalIndex(0);
      }

      // Set initial editing state
      setEditing(initialEditing);
    }
  }, [currentGoal, visible, form, initialEditing]);

  // Handle form submission
  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const updateData = {
        status: values.status,
        mdSmartGoalOrder: values.mdSmartGoalOrder,
        clientFeedback: values.clientFeedback,
        hmReviewNote: values.hmReviewNote
        // Note: Patient goals are updated individually through separate endpoints
      };

      const response = await axios.patch(`/smart-goals/${currentGoal._id}`, updateData);
      
      // Immediately update local state for instant UI feedback
      setCurrentGoal(response.data.data.smartGoal);
      
      message.success('SMART goal updated successfully');
      setEditing(false);
      
      if (onUpdate) {
        onUpdate(response.data.data.smartGoal);
      }
    } catch (error) {
      console.error('Error updating SMART goal:', error);
      message.error('Failed to update SMART goal');
    } finally {
      setLoading(false);
    }
  };

  // Handle review date update
  const handleReviewUpdate = async (nextReviewDate, reviewNotes) => {
    try {
      setLoading(true);
      const response = await axios.patch(`/smart-goals/${currentGoal._id}/review`, {
        nextReviewDate: nextReviewDate.toISOString(),
        reviewNotes
      });
      
      message.success('Review date updated successfully');
      
      if (onUpdate) {
        onUpdate(response.data.data.smartGoal);
      }
    } catch (error) {
      console.error('Error updating review date:', error);
      message.error('Failed to update review date');
    } finally {
      setLoading(false);
    }
  };

  // Handle client feedback update
  const handleClientFeedbackUpdate = async (feedback) => {
    try {
      setLoading(true);
      const response = await axios.patch(`/smart-goals/${currentGoal._id}/client-feedback`, {
        clientFeedback: feedback
      });
      
      message.success('Client feedback updated successfully');
      
      if (onUpdate) {
        onUpdate(response.data.data.smartGoal);
      }
    } catch (error) {
      console.error('Error updating client feedback:', error);
      message.error('Failed to update client feedback');
    } finally {
      setLoading(false);
    }
  };

  // Handle patient goal update
  const handlePatientGoalUpdate = async (patientGoalId, updateData) => {
    try {
      setLoading(true);
      const response = await axios.patch(`/smart-goals/${currentGoal._id}/patient-goals/${patientGoalId}`, updateData);

      message.success('Patient goal updated successfully');

      // Update the current goal state to trigger re-render
      const updatedGoal = response.data.data.smartGoal;
      setCurrentGoal(updatedGoal);

      // Exit edit mode
      setEditingPatientGoalId(null);

      if (onUpdate) {
        onUpdate(updatedGoal);
      }
    } catch (error) {
      console.error('Error updating patient goal:', error);
      message.error('Failed to update patient goal');
    } finally {
      setLoading(false);
    }
  };

  // Handle starting patient goal edit
  const handleStartPatientGoalEdit = (patientGoalId) => {
    setEditingPatientGoalId(patientGoalId);
  };

  // Handle canceling patient goal edit
  const handleCancelPatientGoalEdit = () => {
    setEditingPatientGoalId(null);
  };

  // Handle saving patient goal edit
  const handleSavePatientGoalEdit = async (updateData) => {
    if (editingPatientGoalId) {
      await handlePatientGoalUpdate(editingPatientGoalId, updateData);
    }
  };

  // Handle adding new patient goal
  const handleAddPatientGoal = async (goalData) => {
    try {
      setLoading(true);
      const response = await axios.post(`/smart-goals/${currentGoal._id}/patient-goals`, goalData);

      message.success('Patient goal added successfully');

      // Update the current goal state to trigger re-render
      const updatedGoal = response.data.data.smartGoal;
      setCurrentGoal(updatedGoal);

      // Reset form and hide it
      addGoalForm.resetFields();
      setShowAddGoalForm(false);

      if (onUpdate) {
        onUpdate(updatedGoal);
      }
    } catch (error) {
      console.error('Error adding patient goal:', error);
      message.error('Failed to add patient goal');
    } finally {
      setLoading(false);
    }
  };

  // Handle removing patient goal
  const handleRemovePatientGoal = async (patientGoalId) => {
    try {
      setLoading(true);
      const response = await axios.delete(`/smart-goals/${currentGoal._id}/patient-goals/${patientGoalId}`);

      message.success('Patient goal removed successfully');

      // Update the current goal state to trigger re-render
      const updatedGoal = response.data.data.smartGoal;
      setCurrentGoal(updatedGoal);

      // Exit edit mode if we were editing the deleted goal
      if (editingPatientGoalId === patientGoalId) {
        setEditingPatientGoalId(null);
      }

      // Adjust active index if necessary
      if (activePatientGoalIndex >= updatedGoal.patientGoals.length - 1) {
        setActivePatientGoalIndex(Math.max(0, updatedGoal.patientGoals.length - 2));
      }

      if (onUpdate) {
        onUpdate(response.data.data.smartGoal);
      }
    } catch (error) {
      console.error('Error removing patient goal:', error);

      // Provide more specific error messages
      let errorMessage = 'Failed to remove patient goal';
      if (error.response?.status === 404) {
        errorMessage = 'Patient goal not found. It may have already been deleted.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to delete this patient goal.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }

      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle actions update for specific patient goal (backward compatibility)
  const handleActionsUpdate = (updatedActions) => {
    // For backward compatibility, update the first patient goal's actions
    if (currentGoal.patientGoals && currentGoal.patientGoals.length > 0) {
      handlePatientGoalActionsUpdate(currentGoal.patientGoals[0]._id, updatedActions);
    }
  };

  // Handle actions update for specific patient goal
  const handlePatientGoalActionsUpdate = (patientGoalId, updatedActions) => {
    if (onUpdate) {
      const updatedGoal = { ...currentGoal };
      const patientGoalIndex = updatedGoal.patientGoals.findIndex(pg => pg._id === patientGoalId);
      if (patientGoalIndex !== -1) {
        updatedGoal.patientGoals[patientGoalIndex].actions = updatedActions;
        // Recalculate completion rate for this patient goal
        const completedActions = updatedActions.filter(a => a.completed).length;
        updatedGoal.patientGoals[patientGoalIndex].completionRate =
          updatedActions.length > 0 ? Math.round((completedActions / updatedActions.length) * 100) : 0;

        // Recalculate overall completion rate
        const totalRate = updatedGoal.patientGoals.reduce((sum, pg) => sum + pg.completionRate, 0);
        updatedGoal.overallCompletionRate = Math.round(totalRate / updatedGoal.patientGoals.length);

        onUpdate(updatedGoal);
      }
    }
  };

  if (!goal) return null;

  const getTierLabel = (tier) => {
    const labels = {
      1: 'Tier 1 - Easy (Beginner)',
      2: 'Tier 2 - Moderate',
      3: 'Tier 3 - Challenging',
      4: 'Tier 4 - Difficult (Advanced)'
    };
    return labels[tier] || `Tier ${tier}`;
  };

  // Calculate overall status from patient goals
  const getOverallStatus = () => {
    if (!currentGoal.patientGoals || currentGoal.patientGoals.length === 0) return { isOverdue: false, isReviewOverdue: false, daysRemaining: null };

    let earliestTarget = null;
    let earliestReview = null;
    let isOverdue = false;
    let isReviewOverdue = false;

    currentGoal.patientGoals.forEach(patientGoal => {
      if (patientGoal.timeline?.targetDate) {
        const targetDate = moment(patientGoal.timeline.targetDate);
        if (!earliestTarget || targetDate.isBefore(earliestTarget)) {
          earliestTarget = targetDate;
        }
        if (moment().isAfter(targetDate)) {
          isOverdue = true;
        }
      }

      if (patientGoal.timeline?.nextReviewDate) {
        const reviewDate = moment(patientGoal.timeline.nextReviewDate);
        if (!earliestReview || reviewDate.isBefore(earliestReview)) {
          earliestReview = reviewDate;
        }
        if (moment().isAfter(reviewDate)) {
          isReviewOverdue = true;
        }
      }
    });

    const daysRemaining = earliestTarget ? earliestTarget.diff(moment(), 'days') : null;

    return { isOverdue, isReviewOverdue, daysRemaining };
  };

  const { isOverdue, isReviewOverdue, daysRemaining } = getOverallStatus();

  // Render individual patient goal details
  const renderPatientGoalDetails = (patientGoal, index) => {
    const goalIsOverdue = patientGoal.timeline?.targetDate && moment().isAfter(moment(patientGoal.timeline.targetDate));
    const goalReviewOverdue = patientGoal.timeline?.nextReviewDate && moment().isAfter(moment(patientGoal.timeline.nextReviewDate));
    const goalDaysRemaining = patientGoal.timeline?.targetDate ? moment(patientGoal.timeline.targetDate).diff(moment(), 'days') : null;

    // Check if this patient goal is being edited
    const isEditing = editingPatientGoalId === patientGoal._id;

    if (isEditing) {
      return (
        <PatientGoalEditForm
          patientGoal={patientGoal}
          onSave={handleSavePatientGoalEdit}
          onCancel={handleCancelPatientGoalEdit}
          onDelete={handleRemovePatientGoal}
          loading={loading}
          totalPatientGoals={currentGoal.patientGoals.length}
          readOnly={readOnly}
        />
      );
    }

    return (
      <div>
        {/* Patient Goal Text */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Text strong>SMART Goal:</Text>
            {!readOnly && (
              <Button
                type="text"
                icon={<EditOutlined />}
                size="small"
                onClick={() => handleStartPatientGoalEdit(patientGoal._id)}
              >
                Edit
              </Button>
            )}
          </div>
          <div style={{
            marginTop: 8,
            padding: 12,
            backgroundColor: '#fafafa',
            borderRadius: 6,
            border: '1px solid #e8e8e8'
          }}>
            <Text>{patientGoal.smartGoal}</Text>
          </div>
        </div>

        {/* Timeline and Status */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <div>
              <Text strong>Start Date:</Text>
              <div>{patientGoal.timeline?.startDate ? moment(patientGoal.timeline.startDate).format('MMM DD, YYYY') : 'Not set'}</div>
            </div>
          </Col>
          <Col span={8}>
            <div>
              <Text strong>Target Date:</Text>
              <div style={{ color: goalIsOverdue ? '#f5222d' : 'inherit' }}>
                {patientGoal.timeline?.targetDate ? moment(patientGoal.timeline.targetDate).format('MMM DD, YYYY') : 'Not set'}
                {goalDaysRemaining !== null && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {goalDaysRemaining > 0 ? `${goalDaysRemaining} days remaining` : `${Math.abs(goalDaysRemaining)} days overdue`}
                  </div>
                )}
              </div>
            </div>
          </Col>
          <Col span={8}>
            <div>
              <Text strong>Next Review:</Text>
              <div style={{ color: goalReviewOverdue ? '#fa8c16' : 'inherit' }}>
                {patientGoal.timeline?.nextReviewDate ? moment(patientGoal.timeline.nextReviewDate).format('MMM DD, YYYY') : 'Not set'}
              </div>
            </div>
          </Col>
        </Row>

        {/* Status and Progress */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <div>
              <Text strong>Status:</Text>
              <div>
                <Tag color={STATUS_COLORS[patientGoal.status]}>{patientGoal.status}</Tag>
                {goalIsOverdue && <Tag color="error">Overdue</Tag>}
                {goalReviewOverdue && <Tag color="warning">Review Due</Tag>}
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div>
              <Text strong>Progress:</Text>
              <div style={{ marginTop: 4 }}>
                <Progress
                  percent={patientGoal.completionRate || 0}
                  size="small"
                  status={patientGoal.status === 'Completed' ? 'success' : 'active'}
                />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {patientGoal.actions?.filter(a => a.completed).length || 0} of {patientGoal.actions?.length || 0} actions completed
                </Text>
              </div>
            </div>
          </Col>
        </Row>

        {/* Actions */}
        {patientGoal.actions && patientGoal.actions.length > 0 && (
          <div>
            <Text strong>Action Steps:</Text>
            <List
              size="small"
              style={{ marginTop: 8 }}
              dataSource={patientGoal.actions}
              renderItem={(action, actionIndex) => (
                <List.Item
                  style={{
                    padding: '8px 12px',
                    backgroundColor: action.completed ? '#f6ffed' : '#fff',
                    border: '1px solid #e8e8e8',
                    borderRadius: 4,
                    marginBottom: 4
                  }}
                >
                  <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text style={{ textDecoration: action.completed ? 'line-through' : 'none' }}>
                        {action.description}
                      </Text>
                      <Tag color={action.completed ? 'success' : 'default'}>
                        {action.completed ? 'Completed' : 'Pending'}
                      </Tag>
                    </div>
                    {action.completed && action.completedDate && (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Completed on {moment(action.completedDate).format('MMM DD, YYYY')}
                      </Text>
                    )}
                  </div>
                </List.Item>
              )}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <AimOutlined />
          SMART Goal Details
          <Tag color={STATUS_COLORS[goal.status]}>{goal.status}</Tag>
          {isOverdue && <Tag color="error">Overdue</Tag>}
          {isReviewOverdue && <Tag color="warning">Review Overdue</Tag>}
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={1200}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Close
        </Button>,
        !readOnly && (
          editing ? (
            <Space key="edit-actions">
              <Button onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button type="primary" loading={loading} onClick={handleSave}>
                Save Changes
              </Button>
            </Space>
          ) : (
            <Space key="view-actions">
              <Popconfirm
                title="Delete SMART Goal"
                description={
                  <div>
                    <p>Are you sure you want to delete this SMART goal?</p>
                    <p style={{ marginTop: 8, color: '#ff4d4f', fontWeight: 'bold' }}>
                      This action cannot be undone.
                    </p>
                  </div>
                }
                onConfirm={() => {
                  console.log('Delete confirmed for goal:', goal?._id);
                  onDelete?.(goal);
                }}
                onCancel={() => {
                  console.log('Delete cancelled');
                }}
                okText="Yes, Delete"
                cancelText="Cancel"
                okType="danger"
                placement="topRight"
              >
                <Button danger icon={<DeleteOutlined />}>
                  Delete Goal
                </Button>
              </Popconfirm>
              <Button type="primary" icon={<EditOutlined />} onClick={() => setEditing(true)}>
                Edit Goal
              </Button>
            </Space>
          )
        )
      ].filter(Boolean)}
      destroyOnClose
      className="enhanced-smart-goal-details-modal"
    >
      <Spin spinning={loading}>
        {/* Add Patient Goal Form - Outside main form to avoid nesting */}
        {showAddGoalForm && !readOnly && (
          <Card type="inner" size="small" title="Add New Patient Goal" style={{ marginBottom: 16 }}>
            <Form
              form={addGoalForm}
              layout="vertical"
              onFinish={handleAddPatientGoal}
            >
              <Form.Item
                name="smartGoal"
                label="Patient-Facing Goal"
                rules={[{ required: true, message: 'Please enter the patient-facing goal' }]}
              >
                <Input.TextArea
                  placeholder="Enter a specific, measurable goal for the patient..."
                  rows={3}
                  maxLength={1000}
                  showCount
                />
              </Form.Item>

              <Form.Item
                name="relatedPillar"
                label="Related Pillar"
                rules={[{ required: true, message: 'Please select a pillar' }]}
              >
                <Select placeholder="Select a pillar">
                  <Select.Option value="Medication">Medication</Select.Option>
                  <Select.Option value="Nutrition">Nutrition</Select.Option>
                  <Select.Option value="Movement">Movement</Select.Option>
                  <Select.Option value="Recovery">Recovery</Select.Option>
                  <Select.Option value="Emotion">Emotion</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="tier"
                label="Tier"
                rules={[{ required: true, message: 'Please select a tier' }]}
              >
                <Select placeholder="Select tier">
                  <Select.Option value={1}>Tier 1 - Foundation</Select.Option>
                  <Select.Option value={2}>Tier 2 - Intermediate</Select.Option>
                  <Select.Option value={3}>Tier 3 - Advanced</Select.Option>
                  <Select.Option value={4}>Tier 4 - Expert</Select.Option>
                </Select>
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={['timeline', 'startDate']}
                    label="Start Date"
                  >
                    <MomentDatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={['timeline', 'targetDate']}
                    label="Target Date"
                    rules={[{ required: true, message: 'Please select target date' }]}
                  >
                    <MomentDatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Add Patient Goal
                  </Button>
                  <Button onClick={() => {
                    addGoalForm.resetFields();
                    setShowAddGoalForm(false);
                  }}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        )}

        <Form form={form} layout="vertical">
          {/* MD Clinical Objective Overview */}
          <Card title="MD Clinical Objective" size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Problem: </Text>
                  <Text>{goal.problem?.problemName}</Text>
                  {goal.problem?.icdCode && <Text type="secondary"> ({goal.problem.icdCode})</Text>}
                </div>
                <div>
                  <Text strong>System: </Text>
                  <Text>{ORGAN_SYSTEM_DISPLAY_NAMES[goal.system] || goal.system}</Text>
                </div>
                <div>
                  <Text strong>Overall Progress: </Text>
                  <Progress
                    percent={goal.overallCompletionRate || 0}
                    size="small"
                    status={goal.status === 'Completed' ? 'success' : 'active'}
                  />
                </div>
              </Space>
            </Col>
            <Col xs={24} sm={12}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Patient Goals: </Text>
                  <Text>{currentGoal.patientGoals?.length || 0} goals</Text>
                </div>
                <div>
                  <Text strong>Earliest Target: </Text>
                  <Text style={{ color: isOverdue ? '#f5222d' : 'inherit' }}>
                    {daysRemaining !== null ? (
                      daysRemaining > 0 ? `${daysRemaining} days remaining` : `${Math.abs(daysRemaining)} days overdue`
                    ) : 'Not set'}
                  </Text>
                </div>
                <div>
                  <Text strong>Status: </Text>
                  {editing && !readOnly ? (
                    <Form.Item
                      name="status"
                      style={{ marginBottom: 0, display: 'inline-block', marginLeft: 8 }}
                    >
                      <Select style={{ width: 150 }}>
                        <Option value="Not Started">Not Started</Option>
                        <Option value="In Progress">In Progress</Option>
                        <Option value="Completed">Completed</Option>
                        <Option value="On Hold">On Hold</Option>
                        <Option value="Cancelled">Cancelled</Option>
                      </Select>
                    </Form.Item>
                  ) : (
                    <>
                      <Tag color={STATUS_COLORS[goal.status]}>{goal.status}</Tag>
                      {isOverdue && <Tag color="error">Overdue</Tag>}
                      {isReviewOverdue && <Tag color="warning">Review Overdue</Tag>}
                    </>
                  )}
                </div>
              </Space>
            </Col>
          </Row>

          {/* MD SMART Goal Order */}
          <div style={{ marginTop: 16 }}>
            <Text strong>MD SMART Goal Order:</Text>
            <div style={{
              marginTop: 8,
              padding: 12,
              backgroundColor: '#f5f5f5',
              borderRadius: 6,
              border: '1px solid #d9d9d9'
            }}>
              {editing ? (
                <Form.Item
                  name="mdSmartGoalOrder"
                  style={{ marginBottom: 0 }}
                >
                  <TextArea
                    rows={2}
                    placeholder="Enter MD clinical objective..."
                  />
                </Form.Item>
              ) : (
                <Text>{decodeHtmlEntities(currentGoal.mdSmartGoalOrder)}</Text>
              )}
            </div>
          </div>
          </Card>

          {/* Patient Goals */}
          <Card
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Patient-Facing SMART Goals ({currentGoal.patientGoals?.length || 0})</span>
              {!readOnly && (
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  size="small"
                  onClick={() => setShowAddGoalForm(true)}
                >
                  Add Goal
                </Button>
              )}
            </div>
          }
          size="small"
          style={{ marginBottom: 16 }}
        >

          {currentGoal.patientGoals && currentGoal.patientGoals.length > 0 ? (
            currentGoal.patientGoals.map((patientGoal, index) => (
              <Card
                key={patientGoal._id || index}
                type="inner"
                size="small"
                style={{
                  marginBottom: index < currentGoal.patientGoals.length - 1 ? 16 : 0,
                  border: activePatientGoalIndex === index ? '2px solid #1890ff' : '1px solid #d9d9d9',
                  cursor: 'pointer'
                }}
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Patient Goal {index + 1}</span>
                    <Space>
                      <Tag color={PILLAR_COLORS[patientGoal.relatedPillar]}>
                        {patientGoal.relatedPillar}
                      </Tag>
                      <Tag color={TIER_COLORS[patientGoal.tier]}>
                        Tier {patientGoal.tier}
                      </Tag>
                      <Progress
                        type="circle"
                        size={24}
                        percent={patientGoal.completionRate || 0}
                        format={() => `${patientGoal.completionRate || 0}%`}
                      />
                      {!readOnly && (
                        <Popconfirm
                          title="Delete Patient Goal"
                          description={
                            <div>
                              <p>Are you sure you want to delete this patient goal?</p>
                              <p style={{ marginTop: 8, color: '#ff4d4f', fontWeight: 'bold' }}>
                                This action cannot be undone.
                              </p>
                              {currentGoal.patientGoals.length === 1 && (
                                <p style={{ marginTop: 8, color: '#fa8c16', fontWeight: 'bold' }}>
                                  Warning: This is the last patient goal. Deleting it will leave the SMART goal without any patient-facing goals.
                                </p>
                              )}
                            </div>
                          }
                          onConfirm={() => handleRemovePatientGoal(patientGoal._id)}
                          okText="Yes, Delete"
                          cancelText="Cancel"
                          okType="danger"
                          placement="topRight"
                        >
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                          />
                        </Popconfirm>
                      )}
                    </Space>
                  </div>
                }
                onClick={() => setActivePatientGoalIndex(index)}
              >
                {renderPatientGoalDetails(patientGoal, index)}
              </Card>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
              No patient goals defined yet.
            </div>
          )}
        </Card>

        {/* Action Management for Active Patient Goal */}
          {currentGoal.patientGoals && currentGoal.patientGoals.length > 0 && activePatientGoalIndex < currentGoal.patientGoals.length && (
            <Card
              title={`Action Management - Patient Goal ${activePatientGoalIndex + 1}`}
              size="small"
              style={{ marginBottom: 16 }}
            >
              <ActionManagementComponent
                smartGoal={currentGoal}
                patientGoal={currentGoal.patientGoals[activePatientGoalIndex]}
                onUpdate={(updatedGoal) => {
                  // Update local state with fresh server data
                  setCurrentGoal(updatedGoal);
                  // Notify parent component to refresh
                  if (onUpdate) {
                    onUpdate(updatedGoal);
                  }
                }}
                readOnly={readOnly}
              />
            </Card>
          )}

          {/* Education Materials */}
          <Card title="Education Materials" size="small" style={{ marginBottom: 16 }}>
            <EducationMaterialsManager
              smartGoalId={currentGoal._id}
              patientId={currentGoal.patient}
              materials={currentGoal.educationMaterials || []}
              onMaterialsUpdate={(updatedMaterials) => {
                // Update the goal with new materials and call onUpdate
                const updatedGoal = { ...currentGoal, educationMaterials: updatedMaterials };
                setCurrentGoal(updatedGoal);
                onUpdate(updatedGoal);
              }}
              readOnly={readOnly}
            />
          </Card>

          {/* Review Scheduling */}
          <Card title="Review Scheduling" size="small" style={{ marginBottom: 16 }}>
            <ReviewSchedulingComponent
              smartGoalId={currentGoal._id}
              goal={currentGoal}
              onReviewUpdate={(updatedGoal) => {
                setCurrentGoal(updatedGoal);
                onUpdate(updatedGoal);
              }}
              readOnly={readOnly}
            />
          </Card>

          {/* Notes and Feedback */}
          <Card title="Notes & Feedback" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <div>
                  <Text strong>Client Feedback</Text>
                  <div style={{ marginTop: 8 }}>
                    {editing && !readOnly ? (
                      <Form.Item
                        name="clientFeedback"
                        style={{ marginBottom: 0 }}
                      >
                        <TextArea
                          rows={3}
                          placeholder="Enter client feedback..."
                          maxLength={1000}
                          showCount
                        />
                      </Form.Item>
                    ) : (
                      <div style={{
                        minHeight: 60,
                        padding: 8,
                        backgroundColor: '#fafafa',
                        borderRadius: 4,
                        border: '1px solid #e8e8e8'
                      }}>
                        <Text>{currentGoal.clientFeedback || 'No feedback provided'}</Text>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <Text strong>Health Manager Review Note</Text>
                  <div style={{ marginTop: 8 }}>
                    {editing && !readOnly ? (
                      <Form.Item
                        name="hmReviewNote"
                        style={{ marginBottom: 0 }}
                      >
                        <TextArea
                          rows={3}
                          placeholder="Enter review notes..."
                          maxLength={1000}
                          showCount
                        />
                      </Form.Item>
                    ) : (
                      <div style={{
                        minHeight: 60,
                        padding: 8,
                        backgroundColor: '#fafafa',
                        borderRadius: 4,
                        border: '1px solid #e8e8e8'
                      }}>
                        <Text>{formatDatesInText(decodeHtmlEntities(currentGoal.hmReviewNote)) || 'No review notes'}</Text>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            {/* Goal Metadata */}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e8e8e8' }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>Created: </Text>
                  <Text type="secondary">
                    {moment(goal.createdAt).format('MMM DD, YYYY')} by {goal.createdBy?.name}
                  </Text>
                </Col>
                <Col span={12}>
                  <Text strong>Last Modified: </Text>
                  <Text type="secondary">
                    {goal.updatedAt ? moment(goal.updatedAt).format('MMM DD, YYYY') : 'Not modified'}
                  </Text>
                </Col>
              </Row>
            </div>
          </Card>
        </Form>
      </Spin>
    </Modal>
  );
};

export default EnhancedSmartGoalDetailsModal;
